import React from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { Cancel, Close, ErrorOutline } from "@mui/icons-material";
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useModalStyles } from "./useModalStyles";

interface AlertDialogProps {
  open: boolean;
  content: string;
  onClose: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const ErrorDialog: React.FC<AlertDialogProps> = ({
  open,
  content,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isDark, paperSx, cancelBtnSx } = useModalStyles();

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            ...paperSx(isMobile),
            width: "100%",
            maxWidth: 360,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: isDark ? "rgba(0,0,0,0.72)" : "rgba(15,23,42,0.45)",
            backdropFilter: "blur(3px)",
          },
        }}
        aria-describedby="alert-dialog-slide-description"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.25,
            py: 1.25,
            background: isDark ? "transparent" : "#FAFBFD",
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,.05)" : "#F1F5F9"}`,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "9px",
              background: isDark ? "rgba(239,68,68,.10)" : "#FEF2F2",
              border: `1px solid ${isDark ? "rgba(239,68,68,.18)" : "#FECACA"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ErrorOutline sx={{ fontSize: 15, color: "#EF4444" }} />
          </Box>
          <Typography
            sx={{
              flex: 1,
              fontSize: "0.98rem",
              fontWeight: 700,
              color: isDark ? "#E2E8F0" : "#1E293B",
              lineHeight: 1,
            }}
          >
            Error
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              width: 26,
              height: 26,
              borderRadius: "6px",
              color: isDark ? "#475569" : "#94A3B8",
              "&:hover": { background: isDark ? "rgba(255,255,255,.06)" : "#F1F5F9" },
            }}
          >
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
        <Box sx={{ px: 2.25, py: 2.25 }}>
          <Typography
            id="alert-dialog-slide-description"
            sx={{
              fontSize: "0.92rem",
              color: isDark ? "#94A3B8" : "#64748B",
              lineHeight: 1.7,
            }}
          >
            {content}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 1.5,
            pt: 0.4,
            pb: 0.6,
            background: isDark ? "transparent" : "#FAFBFD",
            borderTop: `1px solid ${isDark ? "rgba(255,255,255,.05)" : "#F1F5F9"}`,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={onClose}
            disableElevation
            sx={{ ...cancelBtnSx, fontSize: "0.98rem", px: 1.25, py: 0.7, minWidth: 86 }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default ErrorDialog;
